import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Action from '@/components/Action';
import Card from '@/components/Card';
import Tabs, { TabDefinition } from '@/components/Tabs';
import { Heading, Subheading } from '@/components/Typography';
import useMediaQuery from '@/hooks/useMediaQuery';

import { parseUuidParam } from '../lib/uuid';

import { CommsBroadcastComposer } from './CommsBroadcastComposer';
import { CommsBroadcastDetail } from './CommsBroadcastDetail';
import { CommsBroadcastsList } from './CommsBroadcastsList';
import { CommsChatDetail } from './CommsChatDetail';
import { CommsChatList } from './CommsChatList';

type ShellTab = 'chats' | 'broadcasts';

const TABS: TabDefinition[] = [
  { a11yLabel: 'Chats', label: 'Chats' },
  { a11yLabel: 'Broadcasts', label: 'Broadcasts' },
];

const containerStyle: React.CSSProperties = {
  display: 'flex',
  height: 'calc(100vh - 56px)',
  width: '100%',
  background: '#f4f5f7',
};

const sideColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
  borderRight: '1px solid #e5e7eb',
};

const detailColumnStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

const listHeaderStyle: React.CSSProperties = {
  padding: '14px 16px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const emptyStateStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f4f5f7',
};

export function CommsAgencyChat() {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationParam = parseUuidParam(searchParams.get('conversation_id'));
  const broadcastParam = parseUuidParam(searchParams.get('broadcast_id'));
  const tabParam = searchParams.get('tab');

  const [tab, setTab] = useState<ShellTab>(() =>
    tabParam === 'broadcasts' || broadcastParam ? 'broadcasts' : 'chats',
  );
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(conversationParam);
  const [selectedBroadcastId, setSelectedBroadcastId] = useState<string | null>(
    broadcastParam,
  );
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => {
    if (conversationParam && conversationParam !== selectedConversationId) {
      setSelectedConversationId(conversationParam);
      setTab('chats');
    }
  }, [conversationParam, selectedConversationId]);

  useEffect(() => {
    if (broadcastParam && broadcastParam !== selectedBroadcastId) {
      setSelectedBroadcastId(broadcastParam);
      setTab('broadcasts');
    }
  }, [broadcastParam, selectedBroadcastId]);

  const updateUrl = useCallback(
    (next: {
      conversationId?: string | null;
      broadcastId?: string | null;
      nextTab?: ShellTab;
    }) => {
      // Functional updater: read prev from React Router so we don't
      // overwrite a stale snapshot if the URL changed between
      // capture-time and apply-time.
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next.conversationId !== undefined) {
            if (next.conversationId)
              params.set('conversation_id', next.conversationId);
            else params.delete('conversation_id');
          }
          if (next.broadcastId !== undefined) {
            if (next.broadcastId) params.set('broadcast_id', next.broadcastId);
            else params.delete('broadcast_id');
          }
          if (next.nextTab) params.set('tab', next.nextTab);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleTabChange = useCallback(
    (idx: number) => {
      const nextTab: ShellTab = idx === 1 ? 'broadcasts' : 'chats';
      setTab(nextTab);
      setComposerOpen(false);
      updateUrl({ nextTab });
    },
    [updateUrl],
  );

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      setSelectedConversationId(conversationId);
      setComposerOpen(false);
      setTab('chats');
      updateUrl({ conversationId, nextTab: 'chats' });
    },
    [updateUrl],
  );

  const handleSelectBroadcast = useCallback(
    (broadcastId: string) => {
      setSelectedBroadcastId(broadcastId);
      setComposerOpen(false);
      updateUrl({ broadcastId });
    },
    [updateUrl],
  );

  const handleOpenComposer = useCallback(() => {
    setComposerOpen(true);
    setSelectedBroadcastId(null);
    updateUrl({ broadcastId: null });
  }, [updateUrl]);

  const handleComposerCreated = useCallback(
    (broadcastId: string) => {
      setComposerOpen(false);
      handleSelectBroadcast(broadcastId);
    },
    [handleSelectBroadcast],
  );

  const handleComposerCancel = useCallback(() => {
    setComposerOpen(false);
  }, []);

  const sideWidth = phoneOnly ? '100%' : 360;
  const sidePane = useMemo(() => {
    return (
      <div style={{ ...sideColumnStyle, width: sideWidth }}>
        <div style={listHeaderStyle}>
          <Heading>Messages</Heading>
          {tab === 'broadcasts' ? (
            <Action.Button
              action={{
                a11yLabel: 'New Broadcast',
                label: 'New',
                id: 'btn-new-broadcast',
                onAction: handleOpenComposer,
              }}
              appearance="primary"
            />
          ) : null}
        </div>
        <Card>
          <Tabs
            fit
            selected={tab === 'broadcasts' ? 1 : 0}
            showTabOnMobile={false}
            tabs={TABS}
            onSelect={handleTabChange}
          />
        </Card>
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {tab === 'chats' ? (
            <CommsChatList
              selectedConversationId={selectedConversationId}
              onSelectConversation={handleSelectConversation}
            />
          ) : (
            <CommsBroadcastsList
              selectedBroadcastId={selectedBroadcastId}
              onSelectBroadcast={handleSelectBroadcast}
            />
          )}
        </div>
      </div>
    );
  }, [
    handleOpenComposer,
    handleSelectBroadcast,
    handleSelectConversation,
    handleTabChange,
    selectedBroadcastId,
    selectedConversationId,
    sideWidth,
    tab,
  ]);

  const detailPane = useMemo(() => {
    if (composerOpen) {
      return (
        <CommsBroadcastComposer
          onCreated={handleComposerCreated}
          onCancel={handleComposerCancel}
        />
      );
    }
    if (tab === 'chats') {
      if (selectedConversationId) {
        return (
          <CommsChatDetail
            conversationId={selectedConversationId}
            onBack={
              phoneOnly
                ? () => {
                    setSelectedConversationId(null);
                    updateUrl({ conversationId: null });
                  }
                : undefined
            }
            phoneOnly={phoneOnly}
          />
        );
      }
      return (
        <div style={emptyStateStyle}>
          <Subheading color="lighter">
            Select a chat to start messaging
          </Subheading>
        </div>
      );
    }
    if (selectedBroadcastId) {
      return <CommsBroadcastDetail broadcastId={selectedBroadcastId} />;
    }
    return (
      <div style={emptyStateStyle}>
        <Subheading color="lighter">
          Select a broadcast to view replies, or send a new one
        </Subheading>
      </div>
    );
  }, [
    composerOpen,
    handleComposerCancel,
    handleComposerCreated,
    handleSelectConversation,
    phoneOnly,
    selectedBroadcastId,
    selectedConversationId,
    tab,
    updateUrl,
  ]);

  if (phoneOnly) {
    const hasDetail =
      composerOpen ||
      (tab === 'chats' && selectedConversationId) ||
      (tab === 'broadcasts' && selectedBroadcastId);
    return (
      <div style={containerStyle}>
        {hasDetail ? (
          <div style={detailColumnStyle}>{detailPane}</div>
        ) : (
          sidePane
        )}
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {sidePane}
      <div style={detailColumnStyle}>{detailPane}</div>
    </div>
  );
}

export default CommsAgencyChat;
